import {
  addYears,
  differenceInDays,
  endOfDay,
  getYear,
  startOfDay,
} from 'date-fns';
import { FirestoreDataConverter, Timestamp } from 'firebase/firestore';
import { Entitlements } from 'shared/types/entitlements';
import { InsightsTimeframes, insightsTimeframes } from 'shared/types/insights';
import { LimitKeys } from 'shared/types/limit-keys';
import { SortingMode, TodayGroupBy } from 'shared/types/sorting-mode';
import { User } from 'shared/types/user';
import { FirestoreUserLimits, UserLimits } from 'shared/types/user-limits';
import {
  FirebaseUserSubscription,
  Interval,
} from 'shared/types/user-subscription';
import { initialWeekStartsOn } from 'shared/types/week-days';
import { getLimits, getPremiumLimits } from 'shared/utils/get-fallback-limits';

import { firebaseTimestampToDate } from './firebase-timestamp-to-date';

const todayGroupByOptions = Object.values(TodayGroupBy);

const processLimits = (
  limits: FirestoreUserLimits | null | undefined,
  isPremium: boolean,
) => {
  const limitBase = isPremium ? getPremiumLimits() : getLimits();
  return Object.keys(limitBase).reduce<UserLimits>((acc, limitKey) => {
    const userLimit = limits?.[limitKey as LimitKeys];
    const limitBackup = limitBase[limitKey as LimitKeys];

    acc[limitKey as LimitKeys] = {
      ...(userLimit ?? limitBackup),
      enforcedAt: userLimit?.enforcedAt
        ? firebaseTimestampToDate(userLimit.enforcedAt)!
        : new Date(),
    };

    return acc;
  }, {} as UserLimits);
};

const processSubscription = (subscription: FirebaseUserSubscription) => {
  const subscriptionDuration = subscription.expiresAt
    ? differenceInDays(
        endOfDay(subscription.expiresAt),
        startOfDay(subscription.purchasedAt),
      )
    : Infinity;

  // decide interval by duration
  let interval = !subscription.expiresAt
    ? Interval.Lifetime
    : subscriptionDuration <= 7
      ? Interval.Week
      : subscriptionDuration <= 14
        ? Interval.TwoWeeks
        : subscriptionDuration <= 31
          ? Interval.Month
          : // lifetime; for now check more than 10 years
            subscriptionDuration > 3_650
            ? Interval.Lifetime
            : Interval.Year;

  return {
    ...subscription,
    // lifetime has no expirationDate
    expiresAt: interval === Interval.Lifetime ? null : subscription.expiresAt,
    // product is custom, we know it is set in BE for lifetime values. So that means it is a trial for lifetime
    interval: subscription.productId?.includes('custom')
      ? Interval.Lifetime
      : interval,
  };
};

export const userConverter: FirestoreDataConverter<User> = {
  fromFirestore: (snapshot) => {
    const data = snapshot.data();

    return {
      ...data,
      createdAt: firebaseTimestampToDate(data.createdAt),
      name: data.name ?? '',
      settings: {
        ...(data.settings ?? {}),
        startOfWeek: data.settings?.startOfWeek ?? initialWeekStartsOn,
        goalTabSorting: {
          ...(data.settings?.goalTabSorting ?? {}),
          all: {
            sorting: data.settings?.goalSorting,
            ...(data.settings?.goalTabSorting?.all ?? {}),
          },
        },
        goalRoadmapSorting: data.settings?.goalRoadmapSorting ?? {
          // add deprecated sorting values if new sorting isn't used already
          inProgress: data.settings?.goalTabSorting?.active?.sorting ?? [],
          [getYear(new Date())]:
            data.settings?.goalTabSorting?.[getYear(new Date())]?.sorting ?? [],
          [getYear(addYears(new Date(), 1))]:
            data.settings?.goalTabSorting?.[getYear(new Date())]?.sorting ?? [],
        },
        todayGroupBy: todayGroupByOptions.includes(data.settings?.todayGroupBy)
          ? data.settings?.todayGroupBy
          : TodayGroupBy.None,
        todoSortingMode: data.settings?.todoSortingMode ?? SortingMode.DueDate,
        insights: {
          ...(data.settings?.insights ?? {}),
          timeframe: data.settings?.insights?.timeframe
            ? insightsTimeframes.includes(data.settings?.insights?.timeframe)
              ? data.settings?.insights?.timeframe
              : InsightsTimeframes.Year
            : null,
        },
      },
      limits: processLimits(
        data.limits,
        !!data.entitlements?.includes(Entitlements.Premium),
      ),
      subscriptions:
        data?.subscriptions?.map((subscription: FirebaseUserSubscription) =>
          processSubscription(subscription),
        ) ?? null,
      user_metadata: {
        ...(data.user_metadata ?? {}),
        lifetime_trial_expires_date: data.user_metadata
          ?.lifetime_trial_expires_date
          ? firebaseTimestampToDate(
              data.user_metadata.lifetime_trial_expires_date,
            )
          : null,
        renewDialogClosed: data.user_metadata?.renewDialogClosed?.length
          ? data.user_metadata.renewDialogClosed.map((date: Timestamp) =>
              firebaseTimestampToDate(date),
            )
          : null,
      },
    } as User;
  },
  toFirestore: (user) => user,
};
